import { Controller } from "@hotwired/stimulus"

// Tri-state bulk label picker for the Change labels sidepanel.
// Initial state comes from data-state on each checkbox: "all" | "some" | "none".
// On submit, only diffs are written as label_states[id]=add|remove.
// While open, selection changes in the table/cards re-fetch this panel with the
// current ids so checkbox states and the saved target set stay in sync.
export default class extends Controller {
  static targets = ["searchInput", "row", "checkbox", "empty", "states", "ids"]
  static values = {
    recordType: String,
    companyId: String,
  }

  connect() {
    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox.dataset.state === "some") {
        checkbox.indeterminate = true
        checkbox.checked = false
      }
    })

    this.boundOnSelectionChanged = this.onSelectionChanged.bind(this)
    document.addEventListener("bulk-selector:selection-changed", this.boundOnSelectionChanged)
    document.addEventListener("checkbox-select-all:selection-changed", this.boundOnSelectionChanged)
  }

  disconnect() {
    document.removeEventListener("bulk-selector:selection-changed", this.boundOnSelectionChanged)
    document.removeEventListener("checkbox-select-all:selection-changed", this.boundOnSelectionChanged)
    clearTimeout(this.refreshTimer)
  }

  search() {
    const query = (this.hasSearchInputTarget ? this.searchInputTarget.value : "").trim().toLowerCase()
    let visibleCount = 0

    this.rowTargets.forEach((row) => {
      const name = (row.dataset.labelName || "").toLowerCase()
      const match = query === "" || name.includes(query)
      row.hidden = !match
      if (match) visibleCount += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = visibleCount > 0
    }
  }

  preventSubmit(event) {
    event.preventDefault()
  }

  prepareSubmit() {
    if (!this.hasStatesTarget) return

    this.statesTarget.innerHTML = ""

    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox.indeterminate) return

      const initial = checkbox.dataset.state
      const id = checkbox.dataset.labelTypeId
      let action = null

      if (checkbox.checked) {
        if (initial !== "all") action = "add"
      } else if (initial !== "none") {
        action = "remove"
      }

      if (!action || !id) return

      const input = document.createElement("input")
      input.type = "hidden"
      input.name = `label_states[${id}]`
      input.value = action
      this.statesTarget.appendChild(input)
    })

    // Clear floating bulk selection so the menu does not reopen after redirect
    // (selection is otherwise restored from sessionStorage on processes/Central KB).
    this.clearFloatingBulkSelection()
  }

  clearFloatingBulkSelection() {
    try {
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith("bulk_selection_"))
        .forEach((key) => {
          sessionStorage.removeItem(key)
          sessionStorage.removeItem(`${key}_timestamp`)
        })
    } catch (_) {
      // sessionStorage may be unavailable
    }

    const bulkSelectorElement = document.querySelector('[data-controller*="bulk-selector"]')
    if (!bulkSelectorElement) return

    const bulkSelectorController = this.application.getControllerForElementAndIdentifier(
      bulkSelectorElement,
      "bulk-selector",
    )
    if (!bulkSelectorController) return

    this.skipSelectionSync = true
    bulkSelectorController.clearSelection()
    this.skipSelectionSync = false
  }

  onSelectionChanged(event) {
    if (!this.element.isConnected || this.skipSelectionSync) return

    const selectedIds = event.detail?.selectedIds
    if (!Array.isArray(selectedIds)) return

    clearTimeout(this.refreshTimer)

    // The hidden ids (and checkbox states) stay on the open panel until the
    // refreshed frame arrives. Block Update for that window so a submit cannot
    // apply the old set. The button lives in the sidepanel footer, outside
    // this form, and is tied to it with the form attribute.
    const idsChanged = !this.sameIds(this.currentIds(), selectedIds)
    this.setSubmitDisabled(idsChanged)
    if (!idsChanged) return

    this.refreshTimer = setTimeout(() => {
      if (!this.element.isConnected) return
      this.refreshForIds(selectedIds)
    }, 150)
  }

  setSubmitDisabled(disabled) {
    const formId = this.element.id
    if (!formId) return

    const submit = document.querySelector(`input[type="submit"][form="${formId}"]`)
    if (submit) submit.disabled = disabled
  }

  refreshForIds(selectedIds) {
    if (!selectedIds.length) {
      this.closeSidepanel()
      return
    }

    if (this.sameIds(this.currentIds(), selectedIds)) return

    const url = new URL("/bulk_operations/change_labels/new", window.location.origin)
    url.searchParams.set("record_type", this.recordTypeValue)
    url.searchParams.set("ids", JSON.stringify(selectedIds.map(String)))

    if (this.companyIdValue) {
      url.searchParams.set("company_id", this.companyIdValue)
    }

    Turbo.visit(url.toString(), { frame: "sidepanel" })
  }

  currentIds() {
    if (!this.hasIdsTarget) return []

    try {
      const parsed = JSON.parse(this.idsTarget.value || "[]")
      return Array.isArray(parsed) ? parsed : []
    } catch (_) {
      return []
    }
  }

  sameIds(a, b) {
    if (a.length !== b.length) return false

    const setA = new Set(a.map(String))
    return b.every((id) => setA.has(String(id)))
  }

  closeSidepanel() {
    const hideControl = this.element
      .closest(".manage_modal")
      ?.querySelector('[data-action*="turbo-modal#hideModal"]')

    if (hideControl) {
      hideControl.click()
      return
    }

    const frame = document.getElementById("sidepanel")
    if (frame) {
      frame.innerHTML = ""
      frame.removeAttribute("src")
    }
  }
}
